import { Box, Card, CardContent, Typography, IconButton, Tooltip, Skeleton } from '@mui/material';
import { CloudUpload, Close } from '@mui/icons-material';
import { FC, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { useResumeCreateMutation, useResumeDeleteMutation, useResumeSelfQuery } from '@carcareers/store';
import { useAuth } from '@carcareers/hooks';
import { DownloadResumeButton } from '@carcareers/components';

const cleanFilename = (filename: string): string => {
  const withoutPrefix = filename.replace(/^resumes\//, '');
  const withoutTimestamp = withoutPrefix.replace(/-\d+\./, '.');
  return withoutTimestamp;
};

export const ResumeCard: FC = () => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDeleted, setIsDeleted] = useState(false);

  const { session } = useAuth();
  const [deleteResume, { isLoading: isDeleting }] = useResumeDeleteMutation();
  const [uploadResume, { isLoading: isUploading }] = useResumeCreateMutation();
  const { data: resume, isLoading } = useResumeSelfQuery();

  const removeFile = async () => {
    if (resume?.id) {
      try {
        await deleteResume(resume.id).unwrap();
        setUploadedFile(null);
        setIsDeleted(true);
      } catch (error) {
        console.error('Failed to delete resume:', error);
      }
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const formData = new FormData();
      formData.append('resumes', file);
      formData.append('jobseekerId', session?.profile?.id?.toString() ?? '');
      try {
        await uploadResume(formData).unwrap();
        setUploadedFile(null);
        setIsDeleted(false);
      } catch (error) {
        console.error('Failed to upload resume:', error);
      }
    }
  }, [uploadResume, session?.profile?.id]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    multiple: false,
    maxFiles: 1,
  });

  return (
    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
      <CardContent sx={{ p: '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
            <CloudUpload sx={{ mr: 1 }} />
            Resume
          </Typography>
        </Box>
        {isLoading || isDeleting || isUploading ? (
          <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 3 }} />
        ) : (resume?.filename || uploadedFile) && !isDeleted ? (
          <>
            <Box
              sx={{
                py: 1,
                pl: 2,
                pr: 1,
                display: 'flex',
                borderRadius: 3,
                alignItems: 'center',
                border: 1,
                borderColor: 'divider',
                backgroundColor: 'grey.50',
                mb: 2,
              }}
            >
              <Typography sx={{ flex: 1, wordBreak: 'break-all' }}>
                {uploadedFile
                  ? uploadedFile.name.length > 40
                    ? `${uploadedFile.name.substring(0, 40)}...`
                    : uploadedFile.name
                  : cleanFilename(resume?.filename || 'Resume file')}
              </Typography>
              {uploadedFile && (
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                  {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                </Typography>
              )}
              <Tooltip title="Remove file" placement="top">
                <IconButton size="small" onClick={removeFile}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {resume?.filename && (
              <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                <DownloadResumeButton resume={resume} variant="text" size="small" />
              </Box>
            )}
          </>
        ) : (
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 3,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'action.hover' : 'transparent',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <input {...getInputProps()} />
            <Typography>
              {isDragActive ? 'Drop your resume here' : 'Drag and drop your resume here, or click to select a file'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Supported formats: PDF, DOC, DOCX (Max 5MB)
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
